'use client'

import * as React from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '../Popover'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../Drawer'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  type AvatarProps,
} from '../Avatar'
import { Switch } from '../Switch'
import { cn } from '../../../lib/utils'
import Icon, { type iconTypes } from '../Icon'
import Link from 'next/link'
import { useIsMobile } from '../Threads/hooks/useMediaQuery'
import { sortWorkspacesByName } from './sortWorkspacesByName'

export interface WorkspaceItem {
  id: string
  name: string
  avatar?: AvatarProps
  isCurrent?: boolean
}

export interface SettingsItem {
  label: string
  href: string
  icon: iconTypes
  isActive?: boolean
}

export interface WorkspaceMenuProps {
  currentWorkspace: {
    name: string
    avatar?: AvatarProps
    href: string
  }
  workplaces: WorkspaceItem[]
  onWorkspaceSwitch: (workspace: WorkspaceItem) => void
  onCreateWorkspace: () => void
  settings: SettingsItem[]
  isDeveloperMode?: boolean
  theme: {
    current: 'light' | 'dark'
    onToggle: () => void
  }
  onSignOut: () => void
}

type ActivePanel = 'workplaces' | 'settings'
type MobileView = 'main' | 'workplaces' | 'settings'

const DESKTOP_MENU_HEIGHT = 400
const DESKTOP_LEFT_COLUMN_WIDTH = 300
const DESKTOP_RIGHT_COLUMN_WIDTH = 220
const menuItemRadius = 'rounded-2xl'
const menuInsetX = 'mx-3'
const menuItemPadding = 'px-4 py-3'
const workplaceRowPadding = 'px-2 py-2'
const workplaceIconSlotClass =
  'flex size-8 shrink-0 items-center justify-center'
const workplaceAvatarClass = 'size-8 rounded-xl shrink-0'

export function WorkspaceMenu({
  currentWorkspace,
  workplaces,
  onWorkspaceSwitch,
  onCreateWorkspace,
  settings,
  isDeveloperMode = false,
  theme,
  onSignOut,
}: WorkspaceMenuProps) {
  const isMobile = useIsMobile()
  const [isOpen, setIsOpen] = React.useState(false)
  const [activePanel, setActivePanel] = React.useState<ActivePanel>('settings')
  const [mobileView, setMobileView] = React.useState<MobileView>('main')

  const otherWorkplaces = sortWorkspacesByName(
    workplaces.filter((w) => !w.isCurrent),
  )

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open)
    if (open) {
      setActivePanel('settings')
    } else {
      setActivePanel('settings')
      setMobileView('main')
    }
  }

  const handleMobileBack = () => {
    setMobileView('main')
  }

  const closeMenu = () => setIsOpen(false)

  const trigger = (
    <button
      className={cn(
        'w-full flex items-center gap-3 pl-3 pr-2 h-12 rounded-md',
        'hover:bg-accent/50 transition-colors duration-200',
        'text-left cursor-pointer',
        'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
      )}
    >
      <Avatar className="h-8 w-8 rounded-md shrink-0">
        <AvatarImage src={currentWorkspace.avatar?.img} />
        <AvatarFallback className="rounded-md text-xs">
          {currentWorkspace.avatar?.initials ||
            currentWorkspace.name.charAt(0).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate text-foreground">
          {currentWorkspace.name}
        </div>
      </div>
      <Icon
        name="ChevronsUpDown"
        className="h-4 w-4 text-muted-foreground shrink-0"
      />
    </button>
  )

  const mobileContent = (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden w-full">
      {mobileView === 'main' && (
        <MobileMainView
          currentWorkspace={currentWorkspace}
          workplacesCount={otherWorkplaces.length}
          onWorkplacesClick={() => setMobileView('workplaces')}
          onSettingsClick={() => setMobileView('settings')}
          onNavigate={closeMenu}
          theme={theme}
          onSignOut={() => {
            onSignOut()
            closeMenu()
          }}
          isDeveloperMode={isDeveloperMode}
        />
      )}
      {mobileView === 'workplaces' && (
        <MobileNestedView title="Workplaces" onBack={handleMobileBack}>
          <WorkplacesPanel
            workplaces={otherWorkplaces}
            onWorkspaceSwitch={(workspace) => {
              onWorkspaceSwitch(workspace)
              closeMenu()
            }}
            onCreateWorkspace={() => {
              onCreateWorkspace()
              closeMenu()
            }}
          />
        </MobileNestedView>
      )}
      {mobileView === 'settings' && (
        <MobileNestedView
          title={isDeveloperMode ? 'Developer Settings' : 'Settings'}
          onBack={handleMobileBack}
        >
          <SettingsPanel settings={settings} onSettingsClick={closeMenu} />
        </MobileNestedView>
      )}
    </div>
  )

  const desktopContent = (
    <div className="flex" style={{ height: DESKTOP_MENU_HEIGHT }}>
      <LeftColumn
        currentWorkspace={currentWorkspace}
        workplacesCount={otherWorkplaces.length}
        activePanel={activePanel}
        onHoverWorkplaces={() => setActivePanel('workplaces')}
        onHoverSettings={() => setActivePanel('settings')}
        theme={theme}
        onSignOut={() => {
          onSignOut()
          closeMenu()
        }}
        isDeveloperMode={isDeveloperMode}
      />
      <RightColumn
        activePanel={activePanel}
        workplaces={otherWorkplaces}
        settings={settings}
        onWorkspaceSwitch={(workspace) => {
          onWorkspaceSwitch(workspace)
          closeMenu()
        }}
        onCreateWorkspace={() => {
          onCreateWorkspace()
          closeMenu()
        }}
        onSettingsClick={closeMenu}
      />
    </div>
  )

  const mobileDrawerTitle =
    mobileView === 'workplaces'
      ? 'Workplaces'
      : mobileView === 'settings'
        ? isDeveloperMode
          ? 'Developer Settings'
          : 'Settings'
        : currentWorkspace.name

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={handleOpenChange}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent className="max-h-[85vh] overflow-hidden p-0">
          <DrawerHeader className="sr-only">
            <DrawerTitle>{mobileDrawerTitle}</DrawerTitle>
          </DrawerHeader>
          <div
            className="flex min-h-0 flex-1 flex-col overflow-hidden px-2 pb-6"
            style={{ maxHeight: 'calc(85vh - 2rem)' }}
          >
            {mobileContent}
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>

      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        className={cn(
          'p-0 w-auto min-w-[200px] rounded-2xl overflow-hidden shadow-lg',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[side=top]:slide-in-from-bottom-2',
        )}
      >
        {desktopContent}
      </PopoverContent>
    </Popover>
  )
}

interface LeftColumnProps {
  currentWorkspace: WorkspaceMenuProps['currentWorkspace']
  workplacesCount: number
  activePanel: ActivePanel
  onHoverWorkplaces: () => void
  onHoverSettings: () => void
  theme: WorkspaceMenuProps['theme']
  onSignOut: () => void
  isDeveloperMode: boolean
}

function LeftColumn({
  currentWorkspace,
  workplacesCount,
  activePanel,
  onHoverWorkplaces,
  onHoverSettings,
  theme,
  onSignOut,
  isDeveloperMode,
}: LeftColumnProps) {
  return (
    <div
      className="flex shrink-0 flex-col py-4 px-1 border-r border-border/60 h-full"
      style={{ width: DESKTOP_LEFT_COLUMN_WIDTH }}
    >
      {/* Current Workspace */}
      <Link
        href={currentWorkspace.href}
        className={cn(
          'flex items-center gap-3',
          menuItemPadding,
          menuInsetX,
          menuItemRadius,
          'hover:bg-accent/50 transition-colors duration-200',
          'text-foreground',
        )}
      >
        <Avatar className="h-7 w-7 rounded-2xl shrink-0">
          <AvatarImage src={currentWorkspace.avatar?.img} />
          <AvatarFallback className="rounded-2xl text-xs">
            {currentWorkspace.avatar?.initials ||
              currentWorkspace.name.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {currentWorkspace.name}
          </div>
          <div className="text-xs text-muted-foreground">Current</div>
        </div>
      </Link>

      <div className="my-3 mx-3 h-px bg-border" />

      {/* Workplaces Row */}
      <button
        onMouseEnter={onHoverWorkplaces}
        className={cn(
          'w-full flex items-center justify-between',
          menuItemPadding,
          menuInsetX,
          menuItemRadius,
          'transition-colors duration-200 text-left',
          'hover:bg-accent/50',
          activePanel === 'workplaces' && 'bg-accent',
          'cursor-pointer',
        )}
        style={{ width: 'calc(100% - 24px)' }}
      >
        <span className="text-sm text-foreground">Workplaces</span>
        <div className="flex items-center gap-2">
          {workplacesCount > 0 && (
            <span className="text-xs text-muted-foreground">
              ({workplacesCount})
            </span>
          )}
          <Icon
            name="ChevronRight"
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform duration-200',
              activePanel === 'workplaces' && 'translate-x-0.5',
            )}
          />
        </div>
      </button>

      {/* Settings Row */}
      <button
        onMouseEnter={onHoverSettings}
        className={cn(
          'w-full flex items-center justify-between',
          menuItemPadding,
          menuInsetX,
          menuItemRadius,
          'transition-colors duration-200 text-left',
          'hover:bg-accent/50',
          activePanel === 'settings' && 'bg-accent',
          'cursor-pointer',
        )}
        style={{ width: 'calc(100% - 24px)' }}
      >
        <span className="text-sm text-foreground">
          {isDeveloperMode ? 'Developer Settings' : 'Settings'}
        </span>
        <Icon
          name="ChevronRight"
          className={cn(
            'h-4 w-4 text-muted-foreground transition-transform duration-200',
            activePanel === 'settings' && 'translate-x-0.5',
          )}
        />
      </button>

      <div className="my-3 mx-3 h-px bg-border" />

      {/* Dark Mode Toggle */}
      <div
        onClick={() => theme.onToggle()}
        className={cn(
          'flex items-center justify-between',
          menuItemPadding,
          menuInsetX,
          menuItemRadius,
          'hover:bg-accent/50 transition-colors duration-200 cursor-pointer',
        )}
        style={{ width: 'calc(100% - 24px)' }}
      >
        <div className="flex items-center gap-3">
          <Icon
            name={theme.current === 'dark' ? 'Sun' : 'Moon'}
            className="h-4 w-4 text-muted-foreground"
          />
          <span className="text-sm text-foreground">
            {theme.current === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </span>
        </div>
        <Switch
          bgSize="small"
          toggleSize="small"
          isChecked={theme.current === 'dark'}
          handleChange={() => theme.onToggle()}
        />
      </div>

      {/* Sign Out */}
      <button
        onClick={onSignOut}
        className={cn(
          'w-full flex items-center gap-3',
          menuItemPadding,
          menuInsetX,
          menuItemRadius,
          'hover:bg-accent/50 transition-colors duration-200 text-left',
          'cursor-pointer',
        )}
        style={{ width: 'calc(100% - 24px)' }}
      >
        <Icon name="LogOut" className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-foreground">Sign Out</span>
      </button>
    </div>
  )
}

interface RightColumnProps {
  activePanel: ActivePanel
  workplaces: WorkspaceItem[]
  settings: SettingsItem[]
  onWorkspaceSwitch: (workspace: WorkspaceItem) => void
  onCreateWorkspace: () => void
  onSettingsClick: () => void
}

function RightColumn({
  activePanel,
  workplaces,
  settings,
  onWorkspaceSwitch,
  onCreateWorkspace,
  onSettingsClick,
}: RightColumnProps) {
  return (
    <div
      className="flex shrink-0 flex-col py-4 px-2 h-full overflow-x-hidden"
      style={{ width: DESKTOP_RIGHT_COLUMN_WIDTH }}
    >
      <div
        key={activePanel}
        className="flex h-full min-h-0 flex-col overflow-x-hidden animate-in fade-in-0 duration-150"
      >
        {activePanel === 'workplaces' ? (
          <WorkplacesPanel
            workplaces={workplaces}
            onWorkspaceSwitch={onWorkspaceSwitch}
            onCreateWorkspace={onCreateWorkspace}
          />
        ) : (
          <SettingsPanel
            settings={settings}
            onSettingsClick={onSettingsClick}
          />
        )}
      </div>
    </div>
  )
}

interface WorkplacesPanelProps {
  workplaces: WorkspaceItem[]
  onWorkspaceSwitch: (workspace: WorkspaceItem) => void
  onCreateWorkspace: () => void
}

function WorkplacesPanel({
  workplaces,
  onWorkspaceSwitch,
  onCreateWorkspace,
}: WorkplacesPanelProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden">
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pb-0.5 pt-1">
        {workplaces.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-6 px-2">
            No other workspaces
          </div>
        ) : (
          <div className="space-y-0.5">
            {workplaces.map((workspace) => (
              <button
                key={workspace.id}
                onClick={() => onWorkspaceSwitch(workspace)}
                className={cn(
                  'w-full flex items-center gap-3 rounded-xl',
                  workplaceRowPadding,
                  'hover:bg-accent/50 transition-colors duration-200',
                  'text-left cursor-pointer',
                )}
              >
                <Avatar className={workplaceAvatarClass}>
                  <AvatarImage src={workspace.avatar?.img} />
                  <AvatarFallback className="rounded-xl text-xs">
                    {workspace.avatar?.initials ||
                      workspace.name.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm text-foreground truncate flex-1">
                  {workspace.name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mt-2 shrink-0 pt-3 px-2 border-t border-border/60">
        <button
          onClick={onCreateWorkspace}
          className={cn(
            'w-full flex items-center gap-3 rounded-xl',
            workplaceRowPadding,
            'hover:bg-accent/50 transition-colors duration-200',
            'text-left cursor-pointer',
          )}
        >
          <div
            className={cn(workplaceIconSlotClass, 'rounded-xl bg-accent/50')}
          >
            <Icon name="Plus" className="h-4 w-4 text-muted-foreground" />
          </div>
          <span className="text-sm text-foreground">New Workplace</span>
        </button>
      </div>
    </div>
  )
}

interface SettingsPanelProps {
  settings: SettingsItem[]
  onSettingsClick: () => void
}

function SettingsPanel({ settings, onSettingsClick }: SettingsPanelProps) {
  return (
    <div className="flex h-full min-h-0 flex-col px-1">
      <div className="space-y-1">
        {settings.map((setting) => (
          <Link
            key={setting.href}
            href={setting.href}
            onClick={onSettingsClick}
            className={cn(
              'flex items-center gap-3 px-4 py-3 rounded-2xl',
              'hover:bg-accent/50 transition-colors duration-200',
              setting.isActive && 'bg-accent/30 text-foreground font-medium',
            )}
          >
            <Icon
              name={setting.icon}
              className="h-4 w-4 shrink-0 text-muted-foreground"
            />
            <span className="text-sm text-foreground">{setting.label}</span>
          </Link>
        ))}
      </div>
    </div>
  )
}

interface MobileMainViewProps {
  currentWorkspace: WorkspaceMenuProps['currentWorkspace']
  workplacesCount: number
  onWorkplacesClick: () => void
  onSettingsClick: () => void
  onNavigate?: () => void
  theme: WorkspaceMenuProps['theme']
  onSignOut: () => void
  isDeveloperMode: boolean
}

function MobileMainView({
  currentWorkspace,
  workplacesCount,
  onWorkplacesClick,
  onSettingsClick,
  onNavigate,
  theme,
  onSignOut,
  isDeveloperMode,
}: MobileMainViewProps) {
  return (
    <div className="w-full py-2">
      {/* Current Workspace */}
      <Link
        href={currentWorkspace.href}
        onClick={onNavigate}
        className={cn(
          'flex items-center gap-3',
          menuItemPadding,
          menuInsetX,
          menuItemRadius,
          'hover:bg-accent/50 transition-colors duration-200',
          'text-foreground',
        )}
      >
        <Avatar className="h-7 w-7 rounded-2xl shrink-0">
          <AvatarImage src={currentWorkspace.avatar?.img} />
          <AvatarFallback className="rounded-2xl text-xs">
            {currentWorkspace.avatar?.initials ||
              currentWorkspace.name.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {currentWorkspace.name}
          </div>
          <div className="text-xs text-muted-foreground">Current</div>
        </div>
      </Link>

      <div className="my-3 mx-3 h-px bg-border" />

      {/* Workplaces */}
      <button
        onClick={onWorkplacesClick}
        className={cn(
          'w-full flex items-center justify-between',
          menuItemPadding,
          menuInsetX,
          menuItemRadius,
          'hover:bg-accent/50 transition-colors duration-200',
          'text-left cursor-pointer',
        )}
        style={{ width: 'calc(100% - 24px)' }}
      >
        <span className="text-sm text-foreground">Workplaces</span>
        <div className="flex items-center gap-2">
          {workplacesCount > 0 && (
            <span className="text-xs text-muted-foreground">
              ({workplacesCount})
            </span>
          )}
          <Icon name="ChevronRight" className="h-4 w-4 text-muted-foreground" />
        </div>
      </button>

      {/* Settings */}
      <button
        onClick={onSettingsClick}
        className={cn(
          'w-full flex items-center justify-between',
          menuItemPadding,
          menuInsetX,
          menuItemRadius,
          'hover:bg-accent/50 transition-colors duration-200',
          'text-left cursor-pointer',
        )}
        style={{ width: 'calc(100% - 24px)' }}
      >
        <span className="text-sm text-foreground">
          {isDeveloperMode ? 'Developer Settings' : 'Settings'}
        </span>
        <Icon name="ChevronRight" className="h-4 w-4 text-muted-foreground" />
      </button>

      <div className="my-3 mx-3 h-px bg-border" />

      {/* Dark Mode Toggle */}
      <div
        onClick={() => theme.onToggle()}
        className={cn(
          'flex items-center justify-between',
          menuItemPadding,
          menuInsetX,
          menuItemRadius,
          'hover:bg-accent/50 transition-colors duration-200 cursor-pointer',
        )}
        style={{ width: 'calc(100% - 24px)' }}
      >
        <div className="flex items-center gap-3">
          <Icon
            name={theme.current === 'dark' ? 'Sun' : 'Moon'}
            className="h-4 w-4 text-muted-foreground"
          />
          <span className="text-sm text-foreground">
            {theme.current === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </span>
        </div>
        <Switch
          bgSize="small"
          toggleSize="small"
          isChecked={theme.current === 'dark'}
          handleChange={() => theme.onToggle()}
        />
      </div>

      {/* Sign Out */}
      <button
        onClick={onSignOut}
        className={cn(
          'w-full flex items-center gap-3',
          menuItemPadding,
          menuInsetX,
          menuItemRadius,
          'hover:bg-accent/50 transition-colors duration-200 text-left',
          'cursor-pointer',
        )}
        style={{ width: 'calc(100% - 24px)' }}
      >
        <Icon name="LogOut" className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-foreground">Sign Out</span>
      </button>
    </div>
  )
}

interface MobileNestedViewProps {
  title: string
  onBack: () => void
  children: React.ReactNode
}

function MobileNestedView({ title, onBack, children }: MobileNestedViewProps) {
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden w-full">
      <button
        onClick={onBack}
        className={cn(
          'w-full flex shrink-0 items-center gap-2 px-4 py-4 border-b border-border',
          'hover:bg-accent/50 transition-colors duration-200',
          'text-left cursor-pointer',
        )}
      >
        <Icon name="ChevronLeft" className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">{title}</span>
      </button>
      <div className="flex min-h-0 flex-1 flex-col overflow-x-hidden py-2 px-1 pt-1">
        {children}
      </div>
    </div>
  )
}
